import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";

import {
  ProblemError,
  TakeOverConflict,
  TakeOverRefused,
  type ReadingReviewOut,
  type Resolution,
  type ResolutionHow,
  type ResolutionOrigin,
} from "@/api";
import { Button } from "@/components/Button";
import { Kbd } from "@/components/Kbd";
import { MaximizeButton } from "@/components/MaximizeButton";
import { PageTitle } from "@/components/PageTitle";
import { SegmentedControl } from "@/components/SegmentedControl";
import { StateMessage } from "@/components/StateMessage";
import { Tooltip } from "@/components/Tooltip";
import { formatMm } from "@/lib/format";
import { LENGTH_REFUSALS, parseLengthMm } from "@/lib/length";
import { clearResolutions, loadResolutions, saveResolutions } from "@/lib/reviewResolutions";
import { useBoxSize } from "@/lib/useBoxSize";
import { useNarrow } from "@/lib/useNarrow";
import { usePageImage } from "@/lib/usePageImage";

import {
  chosenLine,
  countLine,
  hintLines,
  needsYou,
  nextLine,
  RING_CLOSES,
  ringHintParts,
  ringSentence,
  rowsOf,
  type ReviewRow,
  type RowGroup,
  type RowState,
} from "./model";
import { OriginalPane } from "./OriginalPane";
import { ProposalLegend, ProposalPane } from "./ProposalPane";
import styles from "./Review.module.css";
import { RingFixRow, ringFixTaken } from "./RingFixRow";
import { Sentence } from "./Sentence";
import { useDryRun } from "./useDryRun";
import { WallRow } from "./WallRow";

/** How the screen reaches the server — injected, so the dev preview and the tests can stand in. */
export interface ReviewTransport {
  dryRun: (resolutions: Resolution[]) => Promise<ReadingReviewOut>;
  takeOver: (resolutions: Resolution[]) => Promise<void>;
}

interface ReviewScreenProps {
  caseId: string;
  review: ReadingReviewOut;
  pageUrl: string;
  transport: ReviewTransport;
  editorHref: string;
  planningHref: string;
  /**
   * Back to the readings of the form, where a person chose this one (plan 4
   * §10.1: „Andere Lesart“ always goes). `null` where the machine checked it.
   */
  otherReadingHref?: string | null;
  /** Stand 1 is written — on to the board. */
  onTakenOver: () => void;
  /** Read the reading and the Vorgang again (a newer reading, a Stand meanwhile). */
  onReload: () => void;
}

type Problem =
  | { kind: "stand" }
  | { kind: "stale" }
  | { kind: "conflict"; detail: string | null }
  | { kind: "refused"; wallId: string | null; detail: string }
  | { kind: "other"; detail: string };

type Pane = "original" | "proposal";

const GROUPS: readonly RowGroup[] = ["needs", "checked", "unmeasured"];
const GROUP_TITLES: Record<RowGroup, string> = {
  needs: "Braucht dich",
  checked: "Geprüft",
  unmeasured: "Ohne Maß auf dem Blatt",
};

/** The walls the machine read alike, where no ring was checked (model.ts: „geprüft“ only where a check could say no). */
const READ_GROUP_TITLE = "Übereinstimmend gelesen";

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}

/** ⌘ on a Mac, Strg everywhere else — K60 works on Windows. */
function modifierWord(): string {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? "⌘" : "Strg";
}

function without<V>(record: Record<string, V>, key: string): Record<string, V> {
  if (!(key in record)) return record;
  const rest = { ...record };
  delete rest[key];
  return rest;
}

/**
 * Each row's state and how often it changed. A change after arrival lets the
 * row settle in (`WallRow`'s `settle`); the arrival itself stands still.
 */
type Marks = Record<string, { state: RowState; gen: number }>;

function marksOf(rows: ReviewRow[], previous: Marks): Marks {
  return Object.fromEntries(
    rows.map((row) => {
      const was = previous[row.wall.wallId];
      return [row.wall.wallId, was && was.state === row.state ? was : { state: row.state, gen: was ? was.gen + 1 : 0 }];
    }),
  );
}

/** „W3 wurde nicht angenommen: …“ without the words the title already says. */
function restOfRefusal(detail: string, label: string | undefined): string | null {
  if (!label) return detail;
  const rest = detail.replace(new RegExp(`^${label} wurde nicht angenommen[\\s.:,;–—-]*`), "").trim();
  return rest === "" ? null : rest;
}

function setWithout(set: ReadonlySet<string>, key: string): ReadonlySet<string> {
  if (!set.has(key)) return set;
  const rest = new Set(set);
  rest.delete(key);
  return rest;
}

/**
 * „Vorschlag aus deiner Zeichnung“ — the review of a v2 reading (plan 4 §5).
 *
 * The one job of this screen: check, in a minute, what the machine is not
 * sure of. Everything checked stays quiet; what needs a person stands at the
 * top of the list with its reason, its handwriting and its choices. Every
 * figure a person sets is checked again at the server (`dryRun`) — so a ring
 * that closes with it says so — and nothing is written until „Übernehmen und
 * planen“ (A-014: the machine proposes and checks, a person confirms).
 */
export function ReviewScreen({
  caseId,
  review,
  pageUrl,
  transport,
  editorHref,
  planningHref,
  otherReadingHref = null,
  onTakenOver,
  onReload,
}: ReviewScreenProps) {
  const extractionId = review.extractionId;
  const narrow = useNarrow();
  const image = usePageImage(pageUrl);

  // ── What a person set — kept per Vorgang and reading (F5 loses nothing) ──
  const [resolutions, setResolutionsState] = useState<Resolution[]>(() => {
    const known = new Set(review.walls.map((wall) => wall.wallId));
    return loadResolutions(caseId, extractionId).filter((entry) => known.has(entry.wallId));
  });
  const setResolutions = useCallback(
    (next: Resolution[]) => {
      setResolutionsState(next);
      saveResolutions(caseId, extractionId, next);
    },
    [caseId, extractionId],
  );

  const { current, error: dryError } = useDryRun(review, resolutions, transport.dryRun);
  const rows = useMemo(() => rowsOf(review, current, resolutions), [review, current, resolutions]);
  const rowById = useMemo(() => new Map(rows.map((row) => [row.wall.wallId, row])), [rows]);

  // ── The wall in view everywhere at once ─────────────────────────────────
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const activeWallId = hoverId ?? focusId;

  // ── Rows ────────────────────────────────────────────────────────────────
  const [editing, setEditing] = useState<ReadonlySet<string>>(() => new Set());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<RowGroup, boolean>>(() => ({
    needs: true,
    // „Sieh kurz drüber“: where nothing needs a person, the checked walls
    // are what there is to look at.
    checked: !review.walls.some((wall) => wall.status === "offen" || wall.status === "mehrdeutig"),
    unmeasured: false,
  }));
  const fieldRefs = useRef(new Map<string, HTMLInputElement>());
  // A render that only moves the focus („Anderen Wert eintippen“) has
  // nothing else to change — this is what it changes.
  const [, setFocusTick] = useState(0);
  const editRefs = useRef(new Map<string, HTMLButtonElement>());
  const primaryRef = useRef<HTMLButtonElement>(null);
  /** Where the focus goes once the next render has put it on screen; `null` = the primary button. */
  const pendingFocus = useRef<{ wallId: string | null } | null>(null);

  // ── Saying what changed ─────────────────────────────────────────────────
  // Adjusted while rendering, not in an effect: a sentence reaches the live
  // region in the same commit as the change it is about.
  const [announcement, setAnnouncement] = useState("");
  const ring = ringSentence(review, current);
  const [answered, setAnswered] = useState(current);
  if (current !== answered) {
    // A dry run answered. Say what it changed — the ring, the count — and
    // nothing when it changed nothing a person could see.
    const rowsBefore = rowsOf(review, answered, resolutions);
    const before = countLine(rowsBefore, answered);
    const after = countLine(rows, current);
    const nextAfter = nextLine(rows, current.ring);
    const ringBefore = ringSentence(review, answered);
    setAnswered(current);
    const said = [
      ring !== ringBefore ? ring : null,
      after !== before ? after : null,
      nextAfter !== nextLine(rowsBefore, answered.ring) ? nextAfter : null,
    ]
      .filter(Boolean)
      .join(" ");
    if (said !== "") setAnnouncement(said);
  }

  const [marks, setMarks] = useState<Marks>(() => marksOf(rows, {}));
  if (rows.some((row) => marks[row.wall.wallId]?.state !== row.state)) {
    // A wall that now needs a person, in a group that is shut — a checked
    // wall the ring pulled back into doubt: the group opens, in place.
    const shut = rows
      .filter((row) => needsYou(row) && marks[row.wall.wallId]?.state !== row.state && !expanded[row.group])
      .map((row) => row.group);
    setMarks(marksOf(rows, marks));
    if (shut.length > 0) setExpanded((was) => ({ ...was, ...Object.fromEntries(shut.map((group) => [group, true])) }));
  }

  // ── Taking it over ──────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  const [maximized, setMaximized] = useState<Pane | null>(null);
  const [lastPane, setLastPane] = useState<Pane>("proposal");
  const [narrowView, setNarrowView] = useState<Pane>("proposal");

  const headRef = useRef<HTMLElement>(null);
  const head = useBoxSize(headRef, { width: 0, height: 136 });
  const screenRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Wide, the two drawings reach down to the window's edge wherever they
  // begin: on arrival below the masthead and the head, once stuck below the
  // head alone. Written straight onto the element — a re-render of the whole
  // screen per scrolled frame would be paid for in the drawings.
  useEffect(() => {
    const screenElement = screenRef.current;
    if (narrow || !screenElement) return;
    let frame = 0;
    let written = "";
    const measure = () => {
      frame = 0;
      const grid = gridRef.current;
      if (!grid) return;
      const top = Math.max(grid.getBoundingClientRect().top, head.height + 12);
      const value = `${Math.max(352, Math.round(window.innerHeight - top - 16))}px`;
      if (value === written) return;
      written = value;
      screenElement.style.setProperty("--field-block", value);
    };
    const schedule = () => {
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    // Something above may grow without a scroll or a resize — a notice that
    // arrives after the page did. The document's own size says so.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(document.body);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
      screenElement.style.removeProperty("--field-block");
    };
  }, [narrow, head.height]);

  useEffect(() => {
    const wanted = pendingFocus.current;
    if (wanted === null) return;
    pendingFocus.current = null;
    const target =
      wanted.wallId === null
        ? primaryRef.current
        : (fieldRefs.current.get(wanted.wallId) ?? editRefs.current.get(wanted.wallId) ?? null);
    target?.focus();
    target?.scrollIntoView?.({ block: "nearest" });
  });

  // Wide: the first open wall has the focus on arrival, so typing can start
  // at once. Not at 390 px (plan 4 §5) — a keyboard rising over the drawing
  // is the opposite of a calm start — and never over a focus already placed.
  const arrived = useRef(false);
  useEffect(() => {
    if (arrived.current) return;
    arrived.current = true;
    if (narrow) return;
    if (document.activeElement && document.activeElement !== document.body) return;
    const first = rows.find(needsYou);
    if (first) fieldRefs.current.get(first.wall.wallId)?.focus({ preventScroll: true });
  }, [narrow, rows]);

  useEffect(() => {
    if (maximized === null) return;
    const before = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = before;
    };
  }, [maximized]);

  function isOpen(row: ReviewRow): boolean {
    return editing.has(row.wall.wallId) || needsYou(row);
  }

  function nextOpenAfter(wallId: string, list: Resolution[]): string | null {
    const settled = new Set(list.map((entry) => entry.wallId));
    const open = rows.filter((row) => needsYou(row) && !settled.has(row.wall.wallId));
    const at = rows.findIndex((row) => row.wall.wallId === wallId);
    return (open.find((row) => rows.indexOf(row) > at) ?? open[0])?.wall.wallId ?? null;
  }

  /**
   * Sets one wall's figure. Returns the list as it now stands, for ⌘/Strg+Enter.
   * `origin` says where a figure nobody read came from — the ring's
   * suggestion (§10.1) —, so the audit never counts it as a reading.
   */
  function resolve(
    row: ReviewRow,
    valueMm: number,
    how: ResolutionHow,
    jump: boolean,
    origin: ResolutionOrigin | null = null,
    said: string | null = null,
  ): Resolution[] {
    const wallId = row.wall.wallId;
    const arrivedAs = review.walls.find((wall) => wall.wallId === wallId);
    // The read value on a checked wall stays read (plan 4 §4, W3): nothing
    // to record, and „von dir“ would claim a change nobody made.
    const backToRead = arrivedAs?.status === "geprueft" && arrivedAs.valueMm === valueMm;
    const others = resolutions.filter((entry) => entry.wallId !== wallId);
    const same = resolutions.find(
      (entry) => entry.wallId === wallId && entry.valueMm === valueMm && (entry.origin ?? null) === origin,
    );
    const entry: Resolution = origin === null ? { wallId, valueMm, how } : { wallId, valueMm, how, origin };
    const list = backToRead ? others : same ? resolutions : [...others, entry];
    const changed = list.length !== resolutions.length || list.some((each, index) => each !== resolutions[index]);

    if (changed) {
      setResolutions(list);
      const rowsNow = rowsOf(review, current, list);
      const next = nextLine(rowsNow, current.ring);
      setAnnouncement(
        `${said ?? `${row.wall.label} gesetzt: ${formatMm(valueMm)}.`} ${countLine(rowsNow, current)}${next ? ` ${next}` : ""}`,
      );
    }
    setEditing((was) => setWithout(was, wallId));
    setErrors((was) => without(was, wallId));
    if (problem?.kind === "refused" && problem.wallId === wallId) setProblem(null);
    if (jump) pendingFocus.current = { wallId: nextOpenAfter(wallId, list) };
    return list;
  }

  function undo(wallId: string, moveFocus: boolean): void {
    const row = rowById.get(wallId);
    if (!row || row.resolution === null) return;
    setResolutions(resolutions.filter((entry) => entry.wallId !== wallId));
    setAnnouncement(`${row.wall.label} zurückgenommen.`);
    if (moveFocus) pendingFocus.current = { wallId };
  }

  function edit(wallId: string): void {
    setEditing((was) => new Set(was).add(wallId));
    pendingFocus.current = { wallId };
  }

  function cancel(wallId: string): void {
    setEditing((was) => setWithout(was, wallId));
    setErrors((was) => without(was, wallId));
    pendingFocus.current = { wallId };
  }

  /** A wall clicked in either drawing brings its row into reach. */
  function pick(wallId: string): void {
    const row = rowById.get(wallId);
    if (!row) return;
    setExpanded((was) => ({ ...was, [row.group]: true }));
    pendingFocus.current = { wallId };
  }

  async function submit(list: Resolution[]): Promise<void> {
    if (submitting) return;
    setSubmitting(true);
    setProblem(null);
    try {
      await transport.takeOver(list);
      clearResolutions(caseId, extractionId);
      onTakenOver();
    } catch (cause) {
      if (cause instanceof TakeOverConflict) {
        setProblem(
          cause.kind === "stand_exists"
            ? { kind: "stand" }
            : cause.kind === "stale"
              ? { kind: "stale" }
              : { kind: "conflict", detail: cause.detail },
        );
      } else if (cause instanceof TakeOverRefused) {
        setProblem({ kind: "refused", wallId: cause.wallId, detail: cause.detail });
        const row = cause.wallId !== null ? rowById.get(cause.wallId) : undefined;
        if (row) {
          const wallId = row.wall.wallId;
          setErrors((was) => ({ ...was, [wallId]: `${row.wall.label} wurde nicht angenommen. Prüf den Wert.` }));
          setExpanded((was) => ({ ...was, [row.group]: true }));
          if (!needsYou(row)) setEditing((was) => new Set(was).add(wallId));
          pendingFocus.current = { wallId };
        }
      } else {
        setProblem({
          kind: "other",
          detail: cause instanceof ProblemError ? cause.userMessage : cause instanceof Error ? cause.message : String(cause),
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  /** ⌘/Strg+Enter: the figure in the field in hand goes along, then everything is taken over. */
  function submitFromKeys(target: EventTarget | null): void {
    let list = resolutions;
    if (target instanceof HTMLInputElement && target.dataset.wall) {
      const row = rowById.get(target.dataset.wall);
      if (row && target.value.trim() !== "") {
        const parsed = parseLengthMm(target.value);
        if (!parsed.ok) {
          const reason = parsed.reason === "empty" ? "unreadable" : parsed.reason;
          setErrors((was) => ({ ...was, [row.wall.wallId]: LENGTH_REFUSALS[reason] }));
          return;
        }
        list = resolve(row, parsed.mm, "getippt", false);
      }
    }
    void submit(list);
  }

  // The keys that belong to the whole screen. Re-bound every render, so the
  // handler always sees the list as it stands — there is one of it.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.defaultPrevented) return;
      const modifier = event.metaKey || event.ctrlKey;
      if (modifier && event.key === "Enter") {
        event.preventDefault();
        submitFromKeys(event.target);
      } else if (modifier && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "z") {
        if (isTypingTarget(event.target)) return;
        const last = resolutions.at(-1);
        if (!last) return;
        event.preventDefault();
        undo(last.wallId, false);
      } else if (event.key === "Escape" && maximized !== null && !isTypingTarget(event.target)) {
        setMaximized(null);
      } else if (event.key.toLowerCase() === "f" && !modifier && !event.altKey && !isTypingTarget(event.target)) {
        event.preventDefault();
        setMaximized((was) => (was === null ? lastPane : null));
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // ── What the head says ──────────────────────────────────────────────────
  const count = countLine(rows, current);
  const next = nextLine(rows, current.ring);
  const hints = hintLines(review, rows);
  if (dryError !== null && !(dryError instanceof TakeOverConflict)) {
    hints.push("Neu prüfen gerade nicht möglich — deine Werte sind gemerkt.");
  }
  // A dry run that meets a newer reading, or a Stand another tab wrote
  // meanwhile (review M-3), says so like the take-over would.
  const shownProblem: Problem | null =
    problem ??
    (dryError instanceof TakeOverConflict && dryError.kind === "stale"
      ? { kind: "stale" }
      : dryError instanceof TakeOverConflict && dryError.kind === "stand_exists"
        ? { kind: "stand" }
        : null);
  const modifierKey = modifierWord();
  const simplified = review.simplifications.length;
  // One source for the drawing (plan 4 §9.1): what `settle` drew — from the
  // reading on arrival, from the dry run once a person set a figure.
  const drawn = current.proposal ?? null;
  const gapOpen = drawn !== null && drawn.gap !== null && !drawn.closed;
  // The ring's hint and its one suggestion (§10.1), from the latest check.
  // The suggestion goes once its wall carries a person's figure.
  const ringHint = current.ringHint ?? null;
  const hinted = useMemo(() => new Set(ringHint?.wallIds ?? []), [ringHint]);
  const fix = (current.ringFixes ?? []).find((each) => !resolutions.some((entry) => entry.wallId === each.wallId)) ?? null;
  const fixRow = fix !== null ? (rowById.get(fix.wallId) ?? null) : null;
  const chosen = chosenLine(review);

  // The review's one main action, in K60 red (design 2026-10-24 §8.2) —
  // a proposal becomes a Stand only here (A-014).
  const primary = (
    <Tooltip text={`Übernehmen und planen (${modifierKey} Enter)`}>
      <Button ref={primaryRef} variant="primary" busy={submitting} data-role="take-over" onClick={() => void submit(resolutions)}>
        {submitting ? "Übernimmt …" : "Übernehmen und planen"}
      </Button>
    </Tooltip>
  );

  function field(pane: Pane) {
    return (
      <section
        className={styles.field}
        data-pane={pane}
        data-maximized={maximized === pane}
        aria-label={pane === "original" ? "Original" : "Vorschlag"}
        onPointerEnter={() => setLastPane(pane)}
        onFocus={() => setLastPane(pane)}
      >
        <div className={styles.fieldHead}>
          <h2 className="label">{pane === "original" ? "Original" : "Vorschlag"}</h2>
          <span className={styles.fieldNote}>{pane === "original" ? "Seite 1" : "maßstäblich"}</span>
          <MaximizeButton
            pressed={maximized === pane}
            onClick={() => setMaximized((was) => (was === pane ? null : pane))}
            className={styles.maximize}
          />
        </div>
        <div className={styles.fieldBody}>
          {pane === "original" ? (
            <OriginalPane
              image={image}
              page={review}
              walls={review.walls}
              activeWallId={activeWallId}
              simplifications={review.simplifications}
              hinted={hinted}
              onHover={setHoverId}
              onPick={pick}
            />
          ) : drawn ? (
            <ProposalPane
              proposal={drawn}
              rows={rows}
              activeWallId={activeWallId}
              hinted={hinted}
              onHover={setHoverId}
              onPick={pick}
            />
          ) : (
            <div className={styles.paneMessage}>Kein Vorschlag gezeichnet.</div>
          )}
        </div>
        {pane === "proposal" ? (
          <div className={styles.fieldFoot}>
            <ProposalLegend ringChecked={current.ring.checked} gap={gapOpen} />
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <div
      ref={screenRef}
      className={styles.screen}
      data-narrow={narrow}
      style={{ "--review-head": `${head.height}px` } as CSSProperties}
    >
      <header ref={headRef} className={styles.head}>
        <div className={styles.headRow}>
          <div className={styles.headText}>
            {/* In its own display look also under the frame: the review is
                frozen in its layout until 09.11.2026 (M2, final review I-1). */}
            <PageTitle className={styles.title} ownLook>
              Vorschlag aus deiner Zeichnung
            </PageTitle>
            {chosen !== null ? (
              // A person's choice of the form, said as such (§10.1) — not the
              // machine's check — with the way back to the other readings.
              <p className={styles.chosen} data-role="form-chosen">
                <span>{chosen}</span>
                {otherReadingHref !== null ? (
                  <>
                    <span aria-hidden="true"> · </span>
                    <Link className="inline-link" to={otherReadingHref}>
                      Andere Lesart
                    </Link>
                  </>
                ) : null}
              </p>
            ) : null}
            <p className={styles.count} data-role="count">
              {count}
            </p>
            {next !== null ? (
              <p className={styles.countNext} data-role="count-next">
                {next}
              </p>
            ) : null}
          </div>
          <div className={styles.actions}>
            <Link className={narrow ? "inline-link" : styles.secondaryLink} to={editorHref}>
              Im Editor bearbeiten
            </Link>
            {narrow ? null : primary}
          </div>
        </div>
        {hints.length > 0 || simplified > 0 ? (
          <p className={styles.hints}>
            {hints.map((line) => (
              <span key={line}>{line}</span>
            ))}
            {simplified > 0 ? (
              <Tooltip text="Kleine Fasen und Rücksprünge sind begradigt — im Original als Kreis markiert.">
                <span className={styles.chip} tabIndex={0}>
                  {simplified === 1 ? "1 Stelle vereinfacht" : `${simplified} Stellen vereinfacht`}
                </span>
              </Tooltip>
            ) : null}
          </p>
        ) : null}
        {shownProblem ? (
          <ProblemBox problem={shownProblem} rowById={rowById} planningHref={planningHref} onReload={onReload} />
        ) : null}
      </header>

      <div ref={gridRef} className={styles.grid}>
        {narrow ? (
          <div className={styles.narrowFields}>
            <SegmentedControl
              label="Ansicht"
              options={[
                { value: "proposal", label: "Vorschlag" },
                { value: "original", label: "Original" },
              ]}
              value={narrowView}
              onChange={setNarrowView}
            />
            {field(narrowView)}
          </div>
        ) : (
          <>
            {field("original")}
            {field("proposal")}
          </>
        )}

        <section className={styles.list} aria-label="Wände">
          {ringHint !== null ? (
            // One axis misses (§10.1): which, by how much, which walls — no
            // value. The walls lie marked in both drawings.
            <p className={styles.ringLine} data-role="ring-hint">
              <Sentence parts={ringHintParts(ringHint, review.walls)} />
            </p>
          ) : ring !== null ? (
            <p className={styles.ringLine} data-closed={ring === RING_CLOSES}>
              {ring}
            </p>
          ) : null}
          {GROUPS.map((group) => {
            const members = rows.filter((row) => row.group === group);
            if (members.length === 0) return null;
            const open = expanded[group];
            const listId = `review-group-${group}`;
            return (
              <div key={group} className={styles.group} data-group={group}>
                <h2 className={styles.groupHead}>
                  <button
                    type="button"
                    className={styles.groupToggle}
                    aria-expanded={open}
                    aria-controls={listId}
                    onClick={() => setExpanded((was) => ({ ...was, [group]: !open }))}
                  >
                    <span className={styles.chevron} aria-hidden="true" data-open={open} />
                    {group === "checked" && !current.ring.checked ? READ_GROUP_TITLE : GROUP_TITLES[group]}
                    <span className={styles.groupCount}>
                      {" · "}
                      <span className="tabular">{group === "needs" ? open_(members) : members.length}</span>
                      {group !== "needs" && open_(members) > 0 ? (
                        <>
                          {" · "}
                          <span className="tabular">{open_(members)}</span> offen
                        </>
                      ) : null}
                    </span>
                  </button>
                </h2>
                {/* Above the walls of the group its wall stands in — „Braucht
                    dich“ as a rule; a checked wall the ring pulled back into
                    doubt keeps its own group, and the suggestion goes there. */}
                {open && fix !== null && fixRow !== null && fixRow.group === group ? (
                  <RingFixRow
                    fix={fix}
                    wall={fixRow.wall}
                    image={image}
                    page={review}
                    active={activeWallId === fix.wallId}
                    onTake={() => {
                      resolve(fixRow, fix.valueMm, "gewaehlt", true, "ringfix", ringFixTaken(fix, fixRow.wall));
                    }}
                    onTypeOwn={() => {
                      pendingFocus.current = { wallId: fix.wallId };
                      setFocusTick((tick) => tick + 1);
                    }}
                    onActive={(how, on) => {
                      const set = how === "hover" ? setHoverId : setFocusId;
                      set((was) => (on ? fix.wallId : was === fix.wallId ? null : was));
                    }}
                  />
                ) : null}
                <ul id={listId} className={styles.rows} hidden={!open}>
                  {open
                    ? members.map((row) => {
                        const wallId = row.wall.wallId;
                        return (
                          <WallRow
                            key={`${wallId}-${editing.has(wallId) ? "edit" : "view"}`}
                            row={row}
                            open={isOpen(row)}
                            editing={editing.has(wallId)}
                            active={activeWallId === wallId}
                            image={image}
                            page={review}
                            settle={marks[wallId]?.gen ?? 0}
                            error={errors[wallId] ?? null}
                            onError={(message) =>
                              setErrors((was) => (message === null ? without(was, wallId) : { ...was, [wallId]: message }))
                            }
                            onResolve={(valueMm, how, jump) => {
                              resolve(row, valueMm, how, jump);
                            }}
                            onUndo={() => undo(wallId, true)}
                            onEdit={() => edit(wallId)}
                            onCancel={() => cancel(wallId)}
                            onActive={(how, on) => {
                              const set = how === "hover" ? setHoverId : setFocusId;
                              set((was) => (on ? wallId : was === wallId ? null : was));
                            }}
                            fieldRef={(element) => {
                              if (element) fieldRefs.current.set(wallId, element);
                              else fieldRefs.current.delete(wallId);
                            }}
                            editRef={(element) => {
                              if (element) editRefs.current.set(wallId, element);
                              else editRefs.current.delete(wallId);
                            }}
                          />
                        );
                      })
                    : null}
                </ul>
              </div>
            );
          })}
          {narrow ? null : (
            <p className={styles.keys}>
              <span>
                <Kbd>Enter</Kbd> setzt und springt weiter
              </span>
              <span>
                <Kbd>Esc</Kbd> setzt zurück
              </span>
              <span>
                <Kbd>{modifierKey}</Kbd> <Kbd>Enter</Kbd> übernimmt und plant
              </span>
            </p>
          )}
        </section>
      </div>

      {narrow ? (
        <div className={styles.bar}>
          <p className={styles.barText}>{count}</p>
          {primary}
        </div>
      ) : null}

      <p className="visually-hidden" role="status" data-role="announce">
        {announcement}
      </p>
    </div>
  );
}

/** How many of these rows still need a person. */
function open_(rows: ReviewRow[]): number {
  return rows.filter(needsYou).length;
}

function ProblemBox({
  problem,
  rowById,
  planningHref,
  onReload,
}: {
  problem: Problem;
  rowById: Map<string, ReviewRow>;
  planningHref: string;
  onReload: () => void;
}) {
  const reload = (
    <Button variant="leise" flush onClick={onReload}>
      Neu laden
    </Button>
  );
  switch (problem.kind) {
    case "stand":
      return (
        <StateMessage
          tone="conflict"
          live="alert"
          title="Für diesen Vorgang gibt es schon einen Stand."
          action={
            <Link className="inline-link" to={planningHref}>
              Zur Planung
            </Link>
          }
        />
      );
    case "stale":
      return <StateMessage tone="conflict" live="alert" title="Inzwischen liegt eine neuere Lesung vor." action={reload} />;
    case "conflict":
      return (
        <StateMessage tone="conflict" live="alert" title="Nicht übernommen." action={reload}>
          {problem.detail ?? "Der Vorgang hat sich inzwischen geändert."}
        </StateMessage>
      );
    case "refused": {
      const label = problem.wallId !== null ? rowById.get(problem.wallId)?.wall.label : undefined;
      // The server's sentence begins with what the title says (T7-1: not twice).
      return (
        <StateMessage tone="conflict" live="alert" title={label ? `${label} wurde nicht angenommen. Prüf den Wert.` : "Nicht übernommen."}>
          {restOfRefusal(problem.detail, label)}
        </StateMessage>
      );
    }
    case "other":
      return (
        <StateMessage tone="conflict" live="alert" title="Nicht übernommen.">
          {problem.detail}
        </StateMessage>
      );
  }
}
